import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'

var CARSON_USER_ID = 'b85cd872-6750-494a-960e-7b8d32498154'
var CARSON_PHONE = '8109236229'

function formatPhone(raw) {
  var d = (raw || '').replace(/\D/g, '').slice(0, 10)
  if (d.length === 0) return ''
  if (d.length < 4) return '(' + d
  if (d.length < 7) return '(' + d.slice(0, 3) + ') ' + d.slice(3)
  return '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6)
}

function normalizePhone(s) {
  return (s || '').replace(/\D/g, '')
}

function timeAgo(iso) {
  var ms = Date.now() - new Date(iso).getTime()
  if (ms < 60000) return 'just now'
  if (ms < 3600000) return Math.floor(ms / 60000) + 'm ago'
  if (ms < 86400000) return Math.floor(ms / 3600000) + 'h ago'
  return Math.floor(ms / 86400000) + 'd ago'
}

export default function QuickQuestionButton({ userId, profile, storeCustomers }) {
  var [open, setOpen] = useState(false)
  var [form, setForm] = useState({ client_name: '', client_phone: '', client_email: '', question: '', urgency: 'normal' })
  var [submitting, setSubmitting] = useState(false)
  var [error, setError] = useState(null)
  var [toast, setToast] = useState(null)
  var [recent, setRecent] = useState([])
  var [recentLoading, setRecentLoading] = useState(false)
  var [matchedCustomerId, setMatchedCustomerId] = useState(null)

  var fetchRecent = useCallback(async function() {
    if (!userId) return
    setRecentLoading(true)
    var { data, error: err } = await supabase.from('store_quick_questions').select('*')
      .eq('store_user_id', userId)
      .order('created_at', { ascending: false })
      .limit(5)
    if (err) { console.warn('[QuickQuestion] recent fetch failed', err); setRecentLoading(false); return }
    setRecent(data || [])
    setRecentLoading(false)
  }, [userId])

  useEffect(function() {
    if (open) fetchRecent()
  }, [open, fetchRecent])

  // Reset form when modal opens fresh
  useEffect(function() {
    if (!open) {
      setForm({ client_name: '', client_phone: '', client_email: '', question: '', urgency: 'normal' })
      setError(null)
      setMatchedCustomerId(null)
    }
  }, [open])

  // Auto-link a matching existing customer when name or phone matches.
  useEffect(function() {
    if (!storeCustomers || storeCustomers.length === 0) return
    var nameQ = (form.client_name || '').toLowerCase().trim()
    var phoneQ = normalizePhone(form.client_phone)
    if (!nameQ && !phoneQ) { setMatchedCustomerId(null); return }
    var match = storeCustomers.find(function(c) {
      var cn = (c.client_name || '').toLowerCase().trim()
      var cp = normalizePhone(c.client_phone)
      var nameHit = nameQ && cn === nameQ
      var phoneHit = phoneQ && cp === phoneQ && cp.length >= 10
      return nameHit || phoneHit
    })
    setMatchedCustomerId(match ? match.id : null)
  }, [form.client_name, form.client_phone, storeCustomers])

  var setField = function(k, v) { setForm(function(f) { var n = Object.assign({}, f); n[k] = v; return n }) }

  var handleSubmit = async function() {
    setError(null)
    var phoneDigits = normalizePhone(form.client_phone)
    if (phoneDigits.length < 10) { setError('A 10-digit customer phone number is required.'); return }
    setSubmitting(true)
    var storeName = profile?.store_name || profile?.full_name || 'Store Partner'
    var isUrgent = form.urgency === 'urgent'

    try {
      var insertRes = await supabase.from('store_quick_questions').insert({
        store_user_id: userId,
        store_name: storeName,
        customer_id: matchedCustomerId || null,
        client_name: form.client_name || null,
        client_phone: phoneDigits,
        client_email: form.client_email || null,
        question: form.question || null,
        urgency: isUrgent ? 'urgent' : 'normal',
        status: 'new',
      }).select().single()

      if (insertRes.error || !insertRes.data) {
        console.error('[QuickQuestion] insert failed', insertRes.error)
        setError('Could not save: ' + ((insertRes.error && insertRes.error.message) || 'unknown error'))
        setSubmitting(false)
        return
      }

      var savedRow = insertRes.data
      var displayPhone = formatPhone(phoneDigits)
      var who = form.client_name ? form.client_name : 'a customer'

      // Notify Carson in-app — non-blocking but awaited so the row exists
      // before the navigation toast appears.
      try {
        await supabase.from('notifications').insert({
          user_id: CARSON_USER_ID,
          type: 'store_quick_question',
          title: isUrgent ? String.fromCodePoint(0x1F6A8) + ' URGENT: Customer waiting' : 'New Customer to Call',
          message: storeName + ' sent ' + who + ' (' + displayPhone + ') to call' + (form.question ? ': ' + form.question : ''),
          related_id: savedRow.id,
          link: '/store-questions?q=' + savedRow.id,
          business_id: 'pools',
        })
      } catch (e) {
        console.warn('[QuickQuestion] notification insert failed', e)
      }

      // Urgent → fire SMS as well. Failure is non-blocking — the in-app
      // notification is the source of truth.
      if (isUrgent) {
        try {
          await fetch('/api/send-sms', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: CARSON_PHONE,
              business_id: 'pools',
              message: String.fromCodePoint(0x1F6A8) + ' URGENT from ' + storeName + ': Call ' + who + ' at ' + displayPhone + ' now.' + (form.question ? ' ' + form.question : ''),
            }),
          })
        } catch (e) {
          console.warn('[QuickQuestion] urgent SMS failed', e)
        }
      }

      // If linked to an existing customer, also bump their last_contact_date
      // so the customer record reflects activity.
      if (matchedCustomerId) {
        supabase.from('store_customers')
          .update({ last_contact_date: new Date().toISOString().split('T')[0] })
          .eq('id', matchedCustomerId)
          .then(function(r) { if (r.error) console.warn('[QuickQuestion] customer last_contact bump failed', r.error) })
      }

      setOpen(false)
      setToast('Sent to Carson! He\u2019ll reach out to ' + displayPhone + ' soon.')
      setTimeout(function() { setToast(null) }, 5000)
    } catch (e) {
      console.error('[QuickQuestion] submit error', e)
      setError('Something went wrong: ' + ((e && e.message) || e))
    }
    setSubmitting(false)
  }

  return (
    <>
      <button type="button" onClick={function() { setOpen(true) }} style={st.fab} title="Quick Question for Carson">
        <span style={st.fabIcon}>{String.fromCodePoint(0x1F4DE)}</span>
        <span style={st.fabLabel}>QUICK QUESTION</span>
      </button>

      {toast && (
        <div style={st.toast}>{toast}</div>
      )}

      {open && (
        <div style={st.overlay} onClick={function() { if (!submitting) setOpen(false) }}>
          <div style={st.modal} onClick={function(e) { e.stopPropagation() }}>
            <div style={st.modalHeader}>
              <div>
                <div style={st.modalTitle}>QUICK QUESTION FOR CARSON</div>
                <div style={st.modalSub}>Just give me their number and I&rsquo;ll call them.</div>
              </div>
              <button type="button" onClick={function() { setOpen(false) }} style={st.closeBtn}>X</button>
            </div>

            <div style={st.field}>
              <label style={st.label}>CUSTOMER PHONE *</label>
              <input
                value={form.client_phone}
                onChange={function(e) { setField('client_phone', formatPhone(e.target.value)) }}
                placeholder="(810) 555-0123"
                inputMode="tel"
                autoFocus
                style={st.input}
              />
            </div>
            <div style={st.fieldRow}>
              <div style={{ flex: 1 }}>
                <label style={st.label}>CUSTOMER NAME</label>
                <input value={form.client_name} onChange={function(e) { setField('client_name', e.target.value) }} placeholder="Optional" style={st.input} list="qq-customer-names" />
                <datalist id="qq-customer-names">
                  {(storeCustomers || []).map(function(c) {
                    return <option key={c.id} value={c.client_name || ''} />
                  })}
                </datalist>
              </div>
              <div style={{ flex: 1 }}>
                <label style={st.label}>CUSTOMER EMAIL</label>
                <input value={form.client_email} onChange={function(e) { setField('client_email', e.target.value) }} placeholder="Optional" style={st.input} />
              </div>
            </div>
            {matchedCustomerId && (
              <div style={st.matchHint}>{String.fromCodePoint(0x1F517)} Linked to existing customer</div>
            )}
            <div style={st.field}>
              <label style={st.label}>WHAT DO THEY WANT TO KNOW?</label>
              <textarea
                value={form.question}
                onChange={function(e) { setField('question', e.target.value) }}
                placeholder="Optional — Carson can ask them when he calls"
                style={Object.assign({}, st.input, { minHeight: '70px', resize: 'vertical' })}
              />
            </div>

            <label style={st.urgentRow}>
              <input
                type="checkbox"
                checked={form.urgency === 'urgent'}
                onChange={function(e) { setField('urgency', e.target.checked ? 'urgent' : 'normal') }}
                style={{ marginRight: '10px', width: '18px', height: '18px', accentColor: '#e05c5c' }}
              />
              <span><strong style={{ color: '#e05c5c' }}>Urgent</strong> &mdash; they&rsquo;re standing here right now</span>
            </label>

            {error && <div style={st.error}>{error}</div>}

            <div style={st.actionsRow}>
              <button type="button" onClick={function() { setOpen(false) }} disabled={submitting} style={st.cancelBtn}>CANCEL</button>
              <button type="button" onClick={handleSubmit} disabled={submitting} style={st.submitBtn}>
                {submitting ? 'SENDING\u2026' : 'SEND TO CARSON'}
              </button>
            </div>

            {/* Recent questions you've sent */}
            <div style={{ marginTop: '24px', paddingTop: '18px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={st.recentTitle}>RECENT QUESTIONS YOU&rsquo;VE SENT</div>
              {recentLoading ? (
                <div style={st.recentEmpty}>Loading\u2026</div>
              ) : recent.length === 0 ? (
                <div style={st.recentEmpty}>No questions sent yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {recent.map(function(q) {
                    var statusColor = q.status === 'resolved' ? '#4a8c6a' : q.status === 'contacted' ? '#4a6a8c' : q.status === 'closed' ? 'rgba(255,255,255,0.4)' : '#b48c3c'
                    var statusIcon = q.status === 'resolved' ? String.fromCodePoint(0x2713) : q.status === 'contacted' ? String.fromCodePoint(0x25F7) : q.status === 'closed' ? String.fromCodePoint(0x2715) : String.fromCodePoint(0x2022)
                    return (
                      <div key={q.id} style={st.recentRow}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', color: '#ffffff' }}>
                            <span style={{ color: statusColor, marginRight: '6px' }}>{statusIcon}</span>
                            {q.client_name || formatPhone(q.client_phone)}
                          </div>
                          {q.owner_notes && <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '3px', whiteSpace: 'pre-wrap' }}>{q.owner_notes}</div>}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', color: statusColor }}>{(q.status || 'new').toUpperCase()}</div>
                          <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', marginTop: '2px' }}>{timeAgo(q.created_at)}</div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

var st = {
  fab: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    zIndex: 200,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '14px 20px',
    backgroundColor: '#b48c3c',
    border: 'none',
    borderRadius: '999px',
    color: '#0a0f1a',
    fontFamily: "'Barlow Condensed',sans-serif",
    fontSize: '13px',
    fontWeight: 700,
    letterSpacing: '2px',
    cursor: 'pointer',
    boxShadow: '0 8px 24px rgba(0,0,0,0.5), 0 2px 6px rgba(180,140,60,0.4)',
  },
  fabIcon: { fontSize: '18px' },
  fabLabel: { whiteSpace: 'nowrap' },
  toast: {
    position: 'fixed',
    bottom: '90px',
    right: '24px',
    zIndex: 201,
    padding: '12px 18px',
    backgroundColor: 'rgba(74,140,106,0.95)',
    border: '1px solid #4a8c6a',
    borderRadius: '6px',
    color: '#ffffff',
    fontSize: '13px',
    boxShadow: '0 6px 20px rgba(0,0,0,0.5)',
    maxWidth: '320px',
  },
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modal: { backgroundColor: '#0f1420', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '6px', padding: '24px', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflow: 'auto', fontFamily: "'Barlow',sans-serif" },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px', gap: '10px' },
  modalTitle: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '18px', fontWeight: 700, letterSpacing: '2px', color: '#b48c3c' },
  modalSub: { fontSize: '12px', color: 'rgba(255,255,255,0.45)', marginTop: '4px' },
  closeBtn: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '18px', cursor: 'pointer' },
  field: { marginBottom: '12px' },
  fieldRow: { display: 'flex', gap: '10px', marginBottom: '12px' },
  label: { display: 'block', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '14px', fontFamily: "'Barlow',sans-serif", outline: 'none', boxSizing: 'border-box' },
  matchHint: { fontSize: '11px', color: '#4a8c6a', marginBottom: '12px', marginTop: '-4px' },
  urgentRow: { display: 'flex', alignItems: 'center', padding: '10px 12px', backgroundColor: 'rgba(224,92,92,0.06)', border: '1px solid rgba(224,92,92,0.2)', borderRadius: '3px', cursor: 'pointer', fontSize: '13px', color: 'rgba(255,255,255,0.75)' },
  error: { marginTop: '12px', padding: '10px 12px', backgroundColor: 'rgba(224,92,92,0.12)', border: '1px solid rgba(224,92,92,0.3)', borderRadius: '3px', color: '#e05c5c', fontSize: '12px' },
  actionsRow: { display: 'flex', gap: '8px', marginTop: '16px' },
  cancelBtn: { padding: '12px 20px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '3px', color: 'rgba(255,255,255,0.55)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  submitBtn: { flex: 1, padding: '12px', backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },
  recentTitle: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '2px', color: 'rgba(255,255,255,0.35)', marginBottom: '10px' },
  recentRow: { display: 'flex', gap: '12px', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '3px' },
  recentEmpty: { padding: '12px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '12px' },
}
